import { useMemo, useState } from "react";
import {
  useAdminApiScopes,
  useLinkAdminApiKeyOrg,
  useSetAdminApiKeyScopes,
} from "../../hooks/queries";
import type { AdminApiKeyRow } from "../../types/contract";
import { apiMessage, OrgPicker, ScopeChecks, toggleScope } from "./ApiKeyForm";

export type KeyConfirm =
  | { kind: "unlink"; key: AdminApiKeyRow; orgId: string }
  | { kind: "revoke"; key: AdminApiKeyRow }
  | { kind: "rotate"; key: AdminApiKeyRow }
  | { kind: "scopes"; key: AdminApiKeyRow; scopes: string[] };

function closed(row: AdminApiKeyRow): boolean {
  return row.status === "revoked" || row.status === "expired";
}

function stamp(iso: string | null | undefined, empty: string): string {
  if (!iso) return empty;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleDateString();
}

function rotation(
  row: AdminApiKeyRow,
  byId: Map<string, AdminApiKeyRow>,
): string {
  const parts: string[] = [];
  if (row.rotated_to) {
    parts.push(`→ ${byId.get(row.rotated_to)?.prefix ?? row.rotated_to}`);
  }
  if (row.rotated_from) {
    parts.push(`← ${byId.get(row.rotated_from)?.prefix ?? row.rotated_from}`);
  }
  return parts.join(" ") || "—";
}

function OrgCell({
  row,
  enabled,
  off,
  onConfirm,
}: {
  row: AdminApiKeyRow;
  enabled: boolean;
  off: boolean;
  onConfirm: (confirm: KeyConfirm) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const link = useLinkAdminApiKeyOrg();
  return (
    <td className="py-1 pr-2">
      <div className="flex flex-wrap gap-1">
        {row.orgs.map((org) => (
          <span
            key={org.org_id}
            className="inline-flex items-center gap-1 rounded border border-line px-1"
          >
            {org.name || org.org_id}
            <button
              type="button"
              aria-label={`Unlink ${org.name || org.org_id}`}
              disabled={off}
              className="disabled:opacity-50"
              onClick={() =>
                onConfirm({ kind: "unlink", key: row, orgId: org.org_id })
              }
            >
              ×
            </button>
          </span>
        ))}
      </div>
      {!off && (
        <button
          type="button"
          className="text-[10px] text-muted underline"
          onClick={() => setAdding((open) => !open)}
        >
          Add org
        </button>
      )}
      {adding && (
        <OrgPicker
          enabled={enabled}
          exclude={row.orgs.map((org) => org.org_id)}
          onPick={(orgId) =>
            link.mutate(
              { keyId: row.key_id, orgId },
              {
                onSuccess: () => {
                  setError(null);
                  setAdding(false);
                },
                onError: (err) => setError(apiMessage(err)),
              },
            )
          }
        />
      )}
      {error && <p className="text-[11px] text-red-400">{error}</p>}
    </td>
  );
}

function ScopeEditor({
  row,
  enabled,
  onEmpty,
}: {
  row: AdminApiKeyRow;
  enabled: boolean;
  onEmpty: (scopes: string[]) => void;
}) {
  const catalog = useAdminApiScopes(enabled);
  const save = useSetAdminApiKeyScopes();
  const [chosen, setChosen] = useState(row.scopes);
  const [error, setError] = useState<string | null>(null);
  const scopes = Array.isArray(catalog.data) ? catalog.data : [];
  return (
    <div className="mt-1">
      <ScopeChecks
        scopes={scopes}
        selected={chosen}
        onToggle={(scope) => setChosen((cur) => toggleScope(cur, scope))}
      />
      {error && <p className="text-[11px] text-red-400">{error}</p>}
      <button
        type="button"
        disabled={save.isPending}
        className="mt-1 rounded border border-line px-1.5 py-0.5 text-[10px] disabled:opacity-50"
        onClick={() => {
          if (chosen.length === 0) {
            onEmpty(chosen);
            return;
          }
          setError(null);
          save.mutate(
            { keyId: row.key_id, scopes: chosen },
            { onError: (err) => setError(apiMessage(err)) },
          );
        }}
      >
        Save scopes
      </button>
    </div>
  );
}

function ScopeCell({
  row,
  enabled,
  off,
  onConfirm,
}: {
  row: AdminApiKeyRow;
  enabled: boolean;
  off: boolean;
  onConfirm: (confirm: KeyConfirm) => void;
}) {
  const [editing, setEditing] = useState(false);
  return (
    <td className="py-1 pr-2">
      <span>{row.scopes.length ? row.scopes.join(", ") : "none"}</span>
      <button
        type="button"
        className="ml-1 text-[10px] underline disabled:opacity-50"
        disabled={off}
        onClick={() => setEditing((open) => !open)}
      >
        Edit
      </button>
      {editing && (
        <ScopeEditor
          row={row}
          enabled={enabled}
          onEmpty={(scopes) => onConfirm({ kind: "scopes", key: row, scopes })}
        />
      )}
    </td>
  );
}

function KeyRow({
  row,
  byId,
  enabled,
  onConfirm,
}: {
  row: AdminApiKeyRow;
  byId: Map<string, AdminApiKeyRow>;
  enabled: boolean;
  onConfirm: (confirm: KeyConfirm) => void;
}) {
  const off = closed(row);
  return (
    <tr className={off ? "opacity-50" : undefined}>
      <td className="py-1 pr-2 font-mono">{row.prefix}</td>
      <td className="py-1 pr-2">{row.name}</td>
      <OrgCell row={row} enabled={enabled} off={off} onConfirm={onConfirm} />
      <ScopeCell row={row} enabled={enabled} off={off} onConfirm={onConfirm} />
      <td className="py-1 pr-2">{row.max_orders_per_day}</td>
      <td className="py-1 pr-2">{row.status}</td>
      <td className="py-1 pr-2">{stamp(row.created_at, "—")}</td>
      <td className="py-1 pr-2">{stamp(row.last_used_at, "never")}</td>
      <td className="py-1 pr-2">{rotation(row, byId)}</td>
      <td className="py-1">
        <button
          type="button"
          disabled={off}
          className="mr-1 underline disabled:opacity-50"
          onClick={() => onConfirm({ kind: "rotate", key: row })}
        >
          Rotate
        </button>
        <button
          type="button"
          disabled={off}
          className="underline disabled:opacity-50"
          onClick={() => onConfirm({ kind: "revoke", key: row })}
        >
          Revoke
        </button>
      </td>
    </tr>
  );
}

const HEADINGS = [
  "Prefix",
  "Label",
  "Orgs",
  "Scopes",
  "Cap/day",
  "Status",
  "Created",
  "Last used",
  "Rotation",
  "",
];

export function KeysTable({
  rows,
  enabled,
  onConfirm,
}: {
  rows: AdminApiKeyRow[];
  enabled: boolean;
  onConfirm: (confirm: KeyConfirm) => void;
}) {
  const byId = useMemo(
    () => new Map(rows.map((row) => [row.key_id, row])),
    [rows],
  );
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[11px]">
        <thead>
          <tr className="border-b border-line text-left text-[9px] uppercase tracking-wide text-faint">
            {HEADINGS.map((heading) => (
              <th key={heading || "actions"} className="py-1 pr-2 font-medium">
                {heading}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <KeyRow
              key={row.key_id}
              row={row}
              byId={byId}
              enabled={enabled}
              onConfirm={onConfirm}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
